import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Rocket, Stamp, Ticket, Megaphone, Globe, Cloud, Wallet, CreditCard,
  Search, X, ChevronRight, ArrowLeft, HelpCircle, ThumbsUp, ThumbsDown, Loader2, Check, Lightbulb, AlertTriangle, CornerDownRight,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/portal/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/help`;

const ICONS = { Rocket, Stamp, Ticket, Megaphone, Globe, Cloud, Wallet, CreditCard };

const HelpCtx = createContext(null);
export const useHelp = () => useContext(HelpCtx) || { open: () => {}, close: () => {} };

// Endkunden-/Empfänger-Seiten: hier KEINE DIMEDIAL-Hilfe (betrifft die Kunden unserer Kunden)
const HELP_HIDDEN_PREFIXES = [
  "/pass",                  // Kundenkarte (Endkunde) + /pass-scanner
  "/karte/",                // Endkunden-Kundenkarte
  "/k/",                    // Kurzlink (Karte/Scanner)
  "/t/",                    // Ticketshop / Ticketbestellung (Veranstaltung)
  "/v/",                    // öffentliche Veranstalterseite
  "/einlass",               // Ticket-Einlass
  "/scan/",                 // Zugangs-Scan
  "/wallet/hinzufuegen",    // Wallet-Zwischenseite
  "/player",                // Signage-Player
  "/stele/",                // geteilter Signage-Player
  "/cf/",                   // Cloud-Free-Download (Empfänger)
  "/upload/",               // Upload-Link (Empfänger)
  "/vorschau/",             // geteilte Medien-Vorschau
  "/s/",                    // Slug-Dispatch (Kurzlink Karte/Ticket)
  "/homepage/vorschau/",    // geteilte Homepage-Vorschau
  "/masterlayout/vorschau/",// geteilte Master-Vorschau
  "/preview/kampagne",      // geteilte Kampagnen-Vorschau
  "/@",                     // öffentliche Link-in-Bio-Seiten (DIMEDIAL.studio/@name)
];
const isHelpHidden = (pathname) => HELP_HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));

export const HelpProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [initialSlug, setInitialSlug] = useState(null);
  const { pathname } = useLocation();
  const open = useCallback((slug = null) => { setInitialSlug(slug); setIsOpen(true); }, []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const onKey = (e) => {
      const blocked = isHelpHidden(pathname);
      if (blocked) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setIsOpen((v) => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pathname]);

  return (
    <HelpCtx.Provider value={{ open, close }}>
      {children}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="right" overlayClassName="z-[200] help-overlay-dim" className="z-[210] w-full sm:max-w-[460px] p-0 bg-[#F7F3EA] border-l border-[#E6DFCF] overflow-y-auto" data-testid="help-sheet">
          <HelpBody initialSlug={initialSlug} onClose={close} inSheet />
        </SheetContent>
      </Sheet>
    </HelpCtx.Provider>
  );
};

// Schwebender Hilfe-Button + Tastenkürzel ⌘K / Strg+K – nur auf DIMEDIAL-Kundenseiten
export const HelpLauncher = () => {
  const { open } = useHelp();
  const { pathname } = useLocation();
  if (isHelpHidden(pathname)) return null;
  return (
    <button type="button" onClick={() => open()} data-testid="help-launcher"
      className="fixed bottom-5 left-5 z-[60] hidden md:inline-flex items-center gap-2 rounded-full bg-[#111] text-white pl-3 pr-4 py-2.5 text-sm font-semibold shadow-lg hover:-translate-y-0.5 transition-all duration-300">
      <HelpCircle className="w-4 h-4 text-[#FDD105]" /> <span>Hilfe</span>
    </button>
  );
};

const Callout = ({ kind, children }) => {
  const tip = kind === "tipp";
  return (
    <div className={`flex gap-2.5 rounded-xl p-3 text-sm ${tip ? "bg-[#FFF8DD] text-[#6B5800]" : "bg-[#FDECEA] text-[#8A2318]"}`}>
      {tip ? <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
      <span>{children}</span>
    </div>
  );
};

// Wiederverwendbarer Hilfe-Inhalt: Kategorien → Trefferliste → Artikel. Für Slide-over UND /hilfe.
export const HelpBody = ({ initialSlug = null, onClose, inSheet = false }) => {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [modul, setModul] = useState(null);
  const [results, setResults] = useState([]);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fb, setFb] = useState(null);
  const inputRef = useRef(null);
  const debounce = useRef(null);

  useEffect(() => { axios.get(`${API}/categories`).then((r) => setCats(r.data.modules || [])).catch(() => {}); }, []);
  useEffect(() => { if (initialSlug) openArticle(initialSlug); /* eslint-disable-next-line */ }, [initialSlug]);

  const fetchList = useCallback((query, mod) => {
    setLoading(true);
    const p = new URLSearchParams();
    if (query) p.set("q", query);
    if (mod) p.set("modul", mod);
    axios.get(`${API}/articles?${p.toString()}`).then((r) => setResults(r.data.articles || [])).catch(() => setResults([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!q && !modul) { setResults([]); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchList(q, modul), 220);
    return () => clearTimeout(debounce.current);
  }, [q, modul, fetchList]);

  const openArticle = (slug) => {
    setLoading(true); setFb(null);
    axios.get(`${API}/article/${slug}`).then((r) => setArticle(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  const backToList = () => setArticle(null);
  const pickCategory = (key) => { setArticle(null); setQ(""); setModul(key); };
  const resetHome = () => { setArticle(null); setModul(null); setQ(""); setResults([]); };

  const sendFeedback = (hilfreich) => {
    if (!article?.article) return;
    setFb(hilfreich ? "up" : "down");
    axios.post(`${API}/feedback`, { slug: article.article.slug, hilfreich }).catch(() => {});
  };

  const goDeeplink = (href) => { if (onClose) onClose(); navigate(href); };

  return (
    <div className="flex flex-col min-h-full" data-testid="help-body">
      {/* Kopf + Suche */}
      <div className="sticky top-0 z-10 bg-[#F7F3EA]/95 backdrop-blur px-5 pt-5 pb-3 border-b border-[#E6DFCF]">
        <div className="flex items-center justify-between mb-3">
          <button type="button" onClick={resetHome} className="flex items-center gap-2 font-bold text-[#111]" data-testid="help-home">
            <HelpCircle className="w-5 h-5 text-[#111]" /> DIMEDIAL Hilfe
          </button>
          {inSheet && <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-[#ECE5D5]" data-testid="help-close"><X className="w-4 h-4" /></button>}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9281]" />
          <input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setModul(null); setArticle(null); }}
            placeholder="Suchen: Kundenkarte, Veranstaltung, Cloud …" data-testid="help-search"
            className="w-full rounded-full border border-[#E6DFCF] bg-white pl-9 pr-4 py-2.5 text-sm outline-none focus:border-[#FDD105] focus:ring-2 focus:ring-[#FDD105]/40" />
        </div>
      </div>

      <div className="px-5 py-4 flex-1">
        {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#C8C0AD]" /></div>}

        {/* Artikel-Ansicht */}
        {!loading && article?.article && (
          <div data-testid="help-article">
            <button type="button" onClick={backToList} className="inline-flex items-center gap-1 text-sm text-[#6E685D] mb-3 hover:text-[#111]"><ArrowLeft className="w-4 h-4" /> Zurück</button>
            <h2 className="text-xl font-extrabold text-[#111] leading-snug">{article.article.frage}</h2>
            <p className="mt-2 text-[#4A463E]">{article.article.kurzantwort}</p>
            {article.article.schritte?.length > 0 && (
              <ol className="mt-4 space-y-2.5" data-testid="help-steps">
                {article.article.schritte.map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-[#111] text-[#FDD105] text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="text-sm text-[#2A2721] pt-0.5">{s}</span>
                  </li>
                ))}
              </ol>
            )}
            {article.article.tipps?.map((t, i) => <div key={i} className="mt-3"><Callout kind="tipp">{t}</Callout></div>)}

            {article.article.deeplink && user && (
              <button type="button" onClick={() => goDeeplink(article.article.deeplink)} data-testid="help-deeplink"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#FDD105] text-[#111] px-4 py-2.5 text-sm font-bold hover:-translate-y-0.5 transition-transform">
                Jetzt dorthin springen <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {article.related?.length > 0 && (
              <div className="mt-6 border-t border-[#E6DFCF] pt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#9A9281] mb-2">Verwandte Fragen</p>
                <div className="space-y-1.5">
                  {article.related.map((r) => (
                    <button key={r.slug} type="button" onClick={() => openArticle(r.slug)} className="w-full flex items-center justify-between gap-2 text-left text-sm text-[#2A2721] hover:text-[#111] py-1" data-testid={`help-related-${r.slug}`}>
                      <span className="flex items-center gap-2"><CornerDownRight className="w-3.5 h-3.5 text-[#C8C0AD]" /> {r.frage}</span>
                      <ChevronRight className="w-4 h-4 text-[#C8C0AD]" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center gap-3 border-t border-[#E6DFCF] pt-4">
              <span className="text-sm text-[#6E685D]">War das hilfreich?</span>
              <button type="button" onClick={() => sendFeedback(true)} disabled={!!fb} className={`p-2 rounded-full border ${fb === "up" ? "bg-[#E8F5EC] border-[#1C6B3C] text-[#1C6B3C]" : "border-[#E6DFCF] text-[#6E685D] hover:bg-[#ECE5D5]"}`} data-testid="help-fb-up"><ThumbsUp className="w-4 h-4" /></button>
              <button type="button" onClick={() => sendFeedback(false)} disabled={!!fb} className={`p-2 rounded-full border ${fb === "down" ? "bg-[#FDECEA] border-[#B42318] text-[#B42318]" : "border-[#E6DFCF] text-[#6E685D] hover:bg-[#ECE5D5]"}`} data-testid="help-fb-down"><ThumbsDown className="w-4 h-4" /></button>
              {fb && <span className="text-sm text-[#1C6B3C] flex items-center gap-1"><Check className="w-4 h-4" /> Danke!</span>}
            </div>
          </div>
        )}

        {/* Trefferliste */}
        {!loading && !article?.article && (q || modul) && (
          <div data-testid="help-results">
            {modul && <button type="button" onClick={resetHome} className="inline-flex items-center gap-1 text-sm text-[#6E685D] mb-3 hover:text-[#111]"><ArrowLeft className="w-4 h-4" /> Alle Bereiche</button>}
            {results.length === 0 ? (
              <div className="text-center py-10 text-[#8C867B]">
                <p className="font-semibold text-[#2A2721]">Keine Treffer</p>
                <p className="text-sm mt-1">Versuch einen anderen Begriff – z. B. „Stempel“, „Einlass“ oder „Speicher“.</p>
              </div>
            ) : results.map((r, i) => (
              <button key={r.slug} type="button" onClick={() => openArticle(r.slug)} style={{ animationDelay: `${i * 30}ms` }}
                className="w-full text-left bg-white rounded-xl border border-[#EDE6D6] p-3.5 mb-2.5 hover:border-[#FDD105] transition-colors flex items-center justify-between gap-3" data-testid={`help-result-${r.slug}`}>
                <span>
                  <span className="block text-sm font-semibold text-[#111]">{r.frage}</span>
                  <span className="block text-xs text-[#8C867B] mt-0.5 line-clamp-1">{r.kurzantwort}</span>
                </span>
                <ChevronRight className="w-4 h-4 text-[#C8C0AD] shrink-0" />
              </button>
            ))}
          </div>
        )}

        {/* Kategorie-Kacheln (Start) */}
        {!loading && !article?.article && !q && !modul && (
          <div data-testid="help-categories">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#9A9281] mb-3">Bereiche</p>
            <div className="grid grid-cols-2 gap-2.5">
              {cats.map((m) => {
                const Ico = ICONS[m.icon] || HelpCircle;
                return (
                  <button key={m.key} type="button" onClick={() => pickCategory(m.key)} data-testid={`help-cat-${m.key}`}
                    className="text-left bg-white rounded-2xl border border-[#EDE6D6] p-4 hover:border-[#FDD105] hover:-translate-y-0.5 transition-all">
                    <span className="inline-flex w-9 h-9 rounded-xl bg-[#111] text-[#FDD105] items-center justify-center mb-2"><Ico className="w-5 h-5" /></span>
                    <span className="block text-sm font-bold text-[#111] leading-tight">{m.label}</span>
                    <span className="block text-xs text-[#8C867B] mt-0.5">{m.tagline}</span>
                    {m.count > 0 && <span className="mt-1.5 inline-block text-[11px] font-semibold text-[#6E685D] bg-[#F1ECDF] rounded-full px-2 py-0.5" data-testid={`help-cat-count-${m.key}`}>{m.count} {m.count === 1 ? "Antwort" : "Antworten"}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
